import { TASK_JOB_STATUS, TASK_STATUS, type Task, type TaskJob, type TaskStatus } from '@klareospace/pharos-lib-types';
import { findDeliveryJob, findPickupJob } from '@klareospace/pharos-lib-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

export const TASK_TIMELINE_STEP_STATE = {
  DONE: 'done',
  FAILED: 'failed',
  TODO: 'todo',
} as const;
export type TaskTimelineStepState = (typeof TASK_TIMELINE_STEP_STATE)[keyof typeof TASK_TIMELINE_STEP_STATE];

/** Linear progression of non-terminal statuses, used to check "at or after" a milestone. Terminal statuses excluded. */
const TASK_STATUS_PROGRESSION: readonly TaskStatus[] = [
  TASK_STATUS.NEW,
  TASK_STATUS.CARRIER_ASSIGNED,
  TASK_STATUS.CARRIER_ACCEPTED,
  TASK_STATUS.DRIVER_ASSIGNED,
  TASK_STATUS.DRIVER_ACCEPTED,
  TASK_STATUS.WAITING_FOR_PICKUP,
  TASK_STATUS.DRIVING_TO_PICKUP,
  TASK_STATUS.ARRIVED_AT_PICKUP,
  TASK_STATUS.PICKUP_COMPLETED,
  TASK_STATUS.IN_TRANSIT,
  TASK_STATUS.DRIVING_TO_DELIVERY,
  TASK_STATUS.ARRIVED_AT_DELIVERY,
  TASK_STATUS.COMPLETED,
];

// ─── Step State Resolvers ─────────────────────────────────────────────────────

/** Failed when the pickup job reports `failed`; done when it reports `done`, or (fallback) when the status is at/after `pickup_completed`. */
export function resolvePickupStepState(task: Task): TaskTimelineStepState {
  const pickupJob = findPickupJob(task);
  const hasFallbackDone = isAtOrAfter(task.status, TASK_STATUS.PICKUP_COMPLETED);
  return resolveStepState(pickupJob, hasFallbackDone);
}

/** Failed when the delivery job reports `failed`; done when it reports `done`, or (fallback) when the status is `completed`. */
export function resolveDeliveryStepState(task: Task): TaskTimelineStepState {
  const deliveryJob = findDeliveryJob(task);
  return resolveStepState(deliveryJob, task.status === TASK_STATUS.COMPLETED);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Failed when the job reports `failed`; done when it reports `done`, or (fallback) when `hasFallbackDone` is true. */
function resolveStepState(job: TaskJob | undefined, hasFallbackDone: boolean): TaskTimelineStepState {
  if (job?.status === TASK_JOB_STATUS.FAILED) return TASK_TIMELINE_STEP_STATE.FAILED;

  const isDone = job?.status === TASK_JOB_STATUS.DONE || hasFallbackDone;
  return isDone ? TASK_TIMELINE_STEP_STATE.DONE : TASK_TIMELINE_STEP_STATE.TODO;
}

function isAtOrAfter(status: TaskStatus, threshold: TaskStatus): boolean {
  const statusIndex = TASK_STATUS_PROGRESSION.indexOf(status);
  const thresholdIndex = TASK_STATUS_PROGRESSION.indexOf(threshold);
  return statusIndex >= 0 && statusIndex >= thresholdIndex;
}
